import { useEffect, useRef } from "react";

/**
 * Hardware barcode scanners type like a very fast keyboard and finish with Enter. This listens
 * on the whole page and hands complete codes to `onScan`, so a scan works without tapping
 * anything first. Typing into an ordinary field is left alone — except fields marked
 * `data-scan-target`, which forward a scan instead of keeping it.
 */
export function useScanner(onScan: (code: string) => void, enabled = true) {
  const buf = useRef("");
  const last = useRef(0);
  const cb = useRef(onScan);
  cb.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const inField = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (inField && !el!.hasAttribute("data-scan-target")) return;
      const now = performance.now();
      if (now - last.current > 60) buf.current = "";
      last.current = now;
      if (e.key === "Enter") {
        const code = buf.current;
        buf.current = "";
        // A real scan is several characters typed within a few ms of each other.
        if (code.length >= 4) {
          e.preventDefault();
          if (inField) (el as HTMLInputElement).value = "";
          cb.current(code);
        }
        return;
      }
      if (e.key.length === 1) buf.current += e.key;
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [enabled]);
}

/** Short beeps for scan feedback (no audio files needed). */
export function beep(ok = true) {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = ok ? 1320 : 220;
    g.gain.value = 0.08;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + (ok ? 0.08 : 0.25));
    o.onended = () => ctx.close();
  } catch {
    /* no audio */
  }
}
